import React, { useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { ownerSelector } from "../../store/store";
import { PAYMENT_URL } from "../../constants";
import { toast } from "react-toastify";
import { createCheckoutSession, getActivatedTrialMessage } from "../../utils/checkout";
import { refreshBillingSession } from "./billingSession";
import { Button } from "../ui";
import { resolveBillingSource } from "../../utils/billingSource";
import PlanChangeModal from "./PlanChangeModal";
import { canAdjustSeatCount, resolveSeatCount, updateSeats } from "../../utils/seats";

type PricingCardProps = {
  currencySymbol: string;
  amount: number;
  billedLine?: string;
  oldPriceDisplay?: number;
  name: string;
  features: string[];
  discount?: number;
  showDiscount?: boolean;
  planId: string;
  paymentUrl?: string;
  checkoutPlan?: "growth" | "professional";
  checkoutInterval?: "monthly" | "quarterly" | "yearly";
  onOpenTrialModal?: () => void;
  isLaunchFreeBilling?: boolean;
  /** Safepay cancel is final — never offer reactivate */
  isSafepayBilling?: boolean;
  launchTrialLabel?: string;
  featuredLayout?: boolean;
  description?: string;
  footerLine?: string;
};

const PlanCard = ({
  currencySymbol,
  amount,
  billedLine,
  oldPriceDisplay,
  name,
  features,
  discount,
  showDiscount,
  planId,
  checkoutPlan = "growth",
  checkoutInterval = "monthly",
  onOpenTrialModal,
  isLaunchFreeBilling = false,
  isSafepayBilling = false,
  launchTrialLabel = "7-day free trial",
  featuredLayout = false,
  description = "",
  footerLine = "",
}: PricingCardProps) => {
  const { owner, currentUser } = useSelector(ownerSelector);
  const dispatch = useDispatch();
  const billing = resolveBillingSource({ owner, currentUser });
  const billingHistory = Array.isArray((billing as any)?.planhistory)
    ? (billing as any).planhistory
    : [];
  const planhistory = billingHistory[billingHistory.length - 1];
  const isPaidCard = planId !== "Free Plan";
  const legacyPlanName = String((planhistory as any)?.planName || "").toLowerCase();
  const legacyPlanId = String((planhistory as any)?.planId || "").toLowerCase();
  const inferLegacyPlanKey = () => {
    if (
      legacyPlanName.includes("professional") ||
      legacyPlanId.includes("professional")
    ) {
      return "professional";
    }
    if (
      legacyPlanName.includes("growth") ||
      legacyPlanName.includes("premium") ||
      legacyPlanId.includes("growth") ||
      legacyPlanId.includes("premium")
    ) {
      return "growth";
    }
    return "";
  };
  const inferLegacyInterval = () => {
    if (legacyPlanName.includes("year")) return "yearly";
    if (legacyPlanName.includes("quarter")) return "quarterly";
    return "monthly";
  };
  const lastPlanKey =
    String((planhistory as any)?.planKey || "").toLowerCase() || inferLegacyPlanKey();
  const lastInterval =
    String((planhistory as any)?.interval || "").toLowerCase() || inferLegacyInterval();
  const checkoutPlanKey = String(checkoutPlan || "").toLowerCase();
  const checkoutIntervalKey = String(checkoutInterval || "").toLowerCase();
  const isCurrentCardPlan =
    isPaidCard &&
    Boolean(
      lastPlanKey &&
        lastPlanKey === checkoutPlanKey &&
        lastInterval === checkoutIntervalKey
    );
  const isLastPlanActive = Boolean((planhistory as any)?.isActive);
  const isCurrentPlanActive = isCurrentCardPlan && isLastPlanActive;
  const isCancelled = Boolean((planhistory as any)?.isCancelled);
  const isCurrentCancelledPlan = isCurrentCardPlan && isCancelled;
  const pendingPlanChange =
    (billing as any)?.pendingPlanChange ||
    (owner as any)?.userBilling?.pendingPlanChange ||
    (owner as any)?.pendingPlanChange ||
    null;
  const hasPendingPlanChange = pendingPlanChange?.status === "pending";
  const isAwaitingCheckout = pendingPlanChange?.status === "awaiting_checkout";
  const isScheduledForThisCard =
    (hasPendingPlanChange || isAwaitingCheckout) &&
    String(pendingPlanChange?.targetPlanKey || "").toLowerCase() === checkoutPlanKey &&
    String(pendingPlanChange?.targetInterval || "").toLowerCase() === checkoutIntervalKey;
  const isAwaitingCheckoutForThisCard = isAwaitingCheckout && isScheduledForThisCard;

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showEmailRequiredModal, setShowEmailRequiredModal] = useState(false);
  const [showPlanChangeModal, setShowPlanChangeModal] = useState(false);
  const [isResubscribing, setIsResubscribing] = useState(false);
  const [isSwitchingPlan, setIsSwitchingPlan] = useState(false);

  const hasNoPlan = (o: any) =>
    !o || !Array.isArray(o.planhistory) || o.planhistory.length === 0;

  const hasFreePlanOnly = (o: any) => {
    const ph = o?.planhistory;
    if (!Array.isArray(ph) || ph.length === 0) return false;
    const last = ph[ph.length - 1];
    return last?.planName === "Free Plan";
  };

  const hasUsedTrial = (o: any) => {
    const ph = o?.planhistory;
    if (!Array.isArray(ph) || ph.length === 0) return false;
    return ph.some((p: any) => p?.isTrial === true);
  };

  const isActiveTrial =
    Boolean((planhistory as any)?.isTrial) &&
    Boolean((planhistory as any)?.isActive) &&
    !Boolean((planhistory as any)?.isCancelled) &&
    isCurrentCardPlan;

  const hasActivePlan = (o: any) => {
    const ph = o?.planhistory;
    if (!Array.isArray(ph) || ph.length === 0) return false;
    const last = ph[ph.length - 1];
    if (!last) return false;

    const now = new Date();

    const parseDate = (raw: any) => {
      if (!raw) return null;
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    if (last.isTrial) {
      const periodEnd = parseDate(last.periodEndDate);
      const trialEnd = parseDate(last.trialEndDate);
      const trialWindowEnd =
        periodEnd && trialEnd
          ? new Date(Math.max(periodEnd.getTime(), trialEnd.getTime()))
          : periodEnd || trialEnd;
      if (!trialWindowEnd) return true;
      if (trialWindowEnd < now) return false;
      return true;
    }

    const isActiveFlag = Boolean(last.isActive);
    const isCancelledFlag = Boolean(last.isCancelled);
    const periodEndRaw = last.periodEndDate;

    if (!isActiveFlag && !periodEndRaw) return false;

    if (periodEndRaw) {
      const end = new Date(periodEndRaw);
      if (!Number.isNaN(end.getTime()) && end < now) return false;
      return true;
    }

    if (isCancelledFlag) return false;
    return isActiveFlag;
  };

  const stillEntitledOnCancelled =
    isCurrentCancelledPlan && hasActivePlan(billing);

  const shouldUseTrialForUpgrade =
    (hasNoPlan(billing) || hasFreePlanOnly(billing)) && !hasUsedTrial(billing);
  const resolvedUserId =
    (typeof currentUser?.userId === "string" && currentUser.userId) ||
    (typeof (owner as any)?.userId === "string" && (owner as any).userId) ||
    "";
  const resolvedEmail =
    typeof currentUser?.email === "string" ? currentUser.email.trim() : "";
  const intervalLabel = "/month";
  const persistedSeatCount = resolveSeatCount(lastPlanKey || "growth", (planhistory as any)?.seatCount);
  const needsPlanChangeModal =
    isPaidCard &&
    hasActivePlan(billing) &&
    !isCurrentCardPlan &&
    !shouldUseTrialForUpgrade &&
    !isAwaitingCheckoutForThisCard;
  const canManageSeats =
    isPaidCard &&
    isCurrentCardPlan &&
    !isCancelled &&
    !isActiveTrial &&
    canAdjustSeatCount(checkoutPlan) &&
    String(lastPlanKey || "").toLowerCase() === checkoutPlan;

  const startPaidPlanSwitch = async (seatCount?: number, trial: boolean = false) => {
    if (!resolvedUserId) {
      toast.error("User session not loaded. Please refresh and try again.");
      return;
    }
    const checkout = await createCheckoutSession({
      ownerId: owner?._id,
      userId: resolvedUserId,
      plan: checkoutPlan,
      interval: checkoutInterval,
      trial,
      seatCount,
    });
    if (checkout.mode === "scheduled") {
      toast.success(checkout.message || "Plan change scheduled for current period end.");
      await refreshBillingSession({ owner, currentUser, dispatch });
      setShowPlanChangeModal(false);
      return;
    }
    if (checkout.mode === "activated") {
      toast.success(getActivatedTrialMessage(checkout, launchTrialLabel));
      await refreshBillingSession({ owner, currentUser, dispatch });
      setShowPlanChangeModal(false);
      return;
    }
    window.location.href = checkout.url;
  };

  const cancelPlan = async () => {
    if (!owner?._id && !resolvedUserId) {
      toast.error("Account session not loaded. Please refresh and try again.");
      return;
    }
    try {
      const resp = await fetch(`${PAYMENT_URL}/cancelSubscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ownerId: owner?._id, userId: resolvedUserId }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        const msg = data?.error || "Unable to cancel subscription. Please try again.";
        toast.error(msg);
        return;
      }
      toast.success("Subscription will be cancelled at period end.");
      try {
        await refreshBillingSession({ owner, currentUser, dispatch });
      } catch (e) {
        console.warn("[PlanCard] Failed to refresh billing after cancellation", e);
      }
    } catch (e: any) {
      const msg = e?.message || "Unable to cancel subscription. Please try again.";
      toast.error(msg);
    }
  };

  const buttonLabel = isAwaitingCheckoutForThisCard
    ? isResubscribing
      ? "Opening checkout..."
      : "Complete checkout"
    : isScheduledForThisCard && hasPendingPlanChange
    ? "Scheduled Plan"
    : isPaidCard && isCurrentCancelledPlan && stillEntitledOnCancelled
    ? "Cancelled"
    : isPaidCard && isCurrentCancelledPlan
    ? isResubscribing
      ? "Re-subscribing..."
      : "Re-subscribe"
    : isPaidCard && (isCurrentPlanActive || isActiveTrial)
    ? "Current Plan"
    : isLaunchFreeBilling && shouldUseTrialForUpgrade
    ? "Start 1.5-Month Free Trial"
    : shouldUseTrialForUpgrade
    ? "Start 7 Day Free Trial"
    : "Switch to " + name;

  const buttonDisabled =
    (isPaidCard && isCurrentCardPlan && !isCancelled) ||
    (isPaidCard && isCurrentCancelledPlan && stillEntitledOnCancelled) ||
    (isScheduledForThisCard && hasPendingPlanChange && !isAwaitingCheckoutForThisCard) ||
    isResubscribing;

  const handlePlanAction = async () => {
    // Cancelled + still entitled: Safepay cannot reactivate. Block same-plan re-subscribe.
    if (isPaidCard && isCurrentCancelledPlan && stillEntitledOnCancelled) {
      toast.info(
        "This plan is cancelled. Access continues until the period ends. Choose a different plan to schedule a switch, or re-subscribe after access ends."
      );
      return;
    }

    // Awaiting checkout for this target plan → open Safepay (no trial).
    if (isAwaitingCheckoutForThisCard) {
      if (isResubscribing) return;
      try {
        setIsResubscribing(true);
        await startPaidPlanSwitch(undefined, false);
      } catch (e: any) {
        toast.error(e?.message || "Unable to start checkout.");
      } finally {
        setIsResubscribing(false);
      }
      return;
    }

    if (isPaidCard && isCurrentCancelledPlan) {
      const activeNow = hasActivePlan(billing);
      if (isResubscribing) return;
      try {
        setIsResubscribing(true);
        // Safepay: cancel is final — never call reactivate. Checkout only after access ends.
        if (isSafepayBilling || !activeNow) {
          if (!resolvedUserId && !owner?._id) {
            toast.error("User session not loaded. Please refresh and try again.");
            return;
          }
          if (owner?._id) {
            const resp = await fetch(`${PAYMENT_URL}/resubscribe`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ ownerId: owner._id, userId: resolvedUserId }),
            });
            const data = await resp.json();
            if (!resp.ok) {
              toast.error(data?.error || "Unable to start subscription.");
              return;
            }
            if (data?.url) window.location.href = data.url;
            else toast.error("Unable to start subscription. Please try again.");
            return;
          }
          const checkout = await createCheckoutSession({
            ownerId: undefined,
            userId: resolvedUserId,
            plan: checkoutPlan,
            interval: checkoutInterval,
            trial: false,
          });
          if (checkout.mode === "checkout") window.location.href = checkout.url;
          else if (checkout.mode === "scheduled") {
            toast.success(checkout.message || "Plan change scheduled.");
            await refreshBillingSession({ owner, currentUser, dispatch });
          }
          return;
        }

        // Legacy Stripe path: undo cancel_at_period_end while still entitled.
        if (!owner?._id) {
          if (!resolvedUserId) {
            toast.error("User session not loaded. Please refresh and try again.");
            return;
          }
          const resp = await fetch(`${PAYMENT_URL}/reactivateSubscription`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId: resolvedUserId }),
          });
          const data = await resp.json();
          if (!resp.ok) {
            toast.error(data?.error || "Unable to re-activate subscription.");
            return;
          }
          toast.success("Subscription will continue after the current period.");
          await refreshBillingSession({ owner, currentUser, dispatch });
          return;
        }
        if (hasPendingPlanChange && resolvedUserId) {
          const cancelScheduledResp = await fetch(`${PAYMENT_URL}/cancelScheduledPlanChange`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId: resolvedUserId }),
          });
          const cancelScheduledData = await cancelScheduledResp.json();
          if (!cancelScheduledResp.ok) {
            toast.error(cancelScheduledData?.error || "Unable to cancel scheduled plan change.");
            return;
          }
          toast.success("Scheduled plan change cancelled. Current plan will continue.");
          await refreshBillingSession({ owner, currentUser, dispatch });
          return;
        }
        const resp = await fetch(`${PAYMENT_URL}/reactivateSubscription`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ownerId: owner._id, userId: resolvedUserId }),
        });
        const data = await resp.json();
        if (!resp.ok) {
          toast.error(data?.error || "Unable to re-enable subscription.");
          return;
        }
        toast.success("Subscription will continue after the current period.");
        await refreshBillingSession({ owner, currentUser, dispatch });
      } catch (e: any) {
        toast.error(e?.message || "Unable to re-enable subscription.");
      } finally {
        setIsResubscribing(false);
      }
      return;
    }
    if (needsPlanChangeModal) {
      setShowPlanChangeModal(true);
      return;
    }
    if (shouldUseTrialForUpgrade && !resolvedEmail) {
      setShowEmailRequiredModal(true);
      return;
    }
    if (!resolvedUserId) {
      toast.error("User session not loaded. Please refresh and try again.");
      return;
    }
    setShowPlanChangeModal(true);
  };

  const cancelConfirmModal = showCancelConfirm ? (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
      <div className="relative min-w-0 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <button
          type="button"
          aria-label="Close"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          onClick={() => {
            if (isCancelling) return;
            setShowCancelConfirm(false);
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M1 1l12 12M13 1L1 13" />
          </svg>
        </button>
        <p className="pr-8 text-lg font-semibold">Cancel your plan?</p>
        <p className="mt-3 text-sm text-gray-600">
          Your plan will remain active until the end of the current period.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button
            variant="outline"
            size="sm"
            isDisabled={isCancelling}
            onClick={() => {
              if (isCancelling) return;
              setShowCancelConfirm(false);
            }}
          >
            No
          </Button>
          <Button
            variant="btn-red"
            size="sm"
            isDisabled={isCancelling}
            onClick={async () => {
              if (isCancelling) return;
              setIsCancelling(true);
              try {
                await cancelPlan();
              } finally {
                setIsCancelling(false);
                setShowCancelConfirm(false);
              }
            }}
          >
            {isCancelling ? "Cancelling..." : "Yes, cancel"}
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  const planChangeModal = showPlanChangeModal ? (
    <PlanChangeModal
      open={showPlanChangeModal}
      onClose={() => {
        if (isSwitchingPlan) return;
        setShowPlanChangeModal(false);
      }}
      currentPlanKey={lastPlanKey || "growth"}
      currentInterval={lastInterval || "monthly"}
      currentSeatCount={persistedSeatCount}
      targetPlanKey={checkoutPlan}
      targetInterval={checkoutInterval}
      targetPlanName={name}
      currencySymbol={currencySymbol}
      mode={canManageSeats ? "seats" : needsPlanChangeModal ? "schedule" : "new"}
      isTrial={canManageSeats ? false : shouldUseTrialForUpgrade}
      isLaunchFreeBilling={isLaunchFreeBilling}
      launchTrialLabel={launchTrialLabel}
      pendingPlanChange={hasPendingPlanChange ? pendingPlanChange : null}
      onConfirm={async (seatCount) => {
        if (isSwitchingPlan) return;
        try {
          setIsSwitchingPlan(true);
          if (canManageSeats) {
            const result = await updateSeats({
              ownerId: owner?._id,
              userId: resolvedUserId,
              seatCount,
            });
            toast.success(result?.message || "Seats updated.");
            await refreshBillingSession({ owner, currentUser, dispatch });
            setShowPlanChangeModal(false);
            return;
          }
          await startPaidPlanSwitch(seatCount, shouldUseTrialForUpgrade);
        } catch (e: any) {
          const msg = e?.message || "Unable to start plan.";
          toast.error(msg);
          // Backend already has the plan; stale UI still showed trial CTA — sync now.
          if (/already on this plan/i.test(String(msg))) {
            await refreshBillingSession({ owner, currentUser, dispatch }).catch(() => {});
            setShowPlanChangeModal(false);
          }
        } finally {
          setIsSwitchingPlan(false);
        }
      }}
    />
  ) : null;

  const emailRequiredModal = showEmailRequiredModal ? (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white px-6 py-5 shadow-lg">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Email required</h2>
          <button
            onClick={() => setShowEmailRequiredModal(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        <p className="text-sm text-gray-700">
          Please add your email to continue payments and billing.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowEmailRequiredModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setShowEmailRequiredModal(false);
              window.location.href = "/settings/profile";
            }}
          >
            Go to Settings
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  const planActionButton =
    planId !== "Free Plan" ? (
      <div className="flex w-full flex-col gap-2">
        <Button
          variant="primary"
          size="sm"
          fullWidth={!featuredLayout}
          onClick={handlePlanAction}
          isDisabled={buttonDisabled}
        >
          {buttonLabel}
        </Button>
        {canManageSeats ? (
          <button
            type="button"
            onClick={() => setShowPlanChangeModal(true)}
            className="w-full rounded-md border border-light-grey bg-white px-3 py-2 text-sm font-medium text-mainColor hover:bg-background-grey"
          >
            Manage seats
          </button>
        ) : null}
      </div>
    ) : null;

  const statusBadge =
    isPaidCard && (isCurrentPlanActive || isActiveTrial) && !isCancelled ? (
      <span className="rounded-md bg-mainColor px-2 py-1 text-xs font-semibold text-white">
        Current Plan
      </span>
    ) : isPaidCard && isCurrentCancelledPlan && !isActiveTrial ? (
      <span
        className={`rounded-md px-2 py-1 text-xs font-semibold ${
          hasActivePlan(billing) ? "bg-red-500 text-white" : "bg-gray-400 text-white"
        }`}
      >
        {hasActivePlan(billing) ? "Cancelled" : "Expired"}
      </span>
    ) : null;

  if (featuredLayout) {
    return (
      <>
        {cancelConfirmModal}
        {planChangeModal}
        {emailRequiredModal}
        <div className="mx-auto w-full max-w-4xl overflow-hidden rounded-2xl border border-mainColor/15 bg-white shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
          <div className="grid lg:grid-cols-[minmax(280px,340px)_1fr]">
            <div className="flex flex-col justify-between border-b border-light-grey bg-gradient-to-br from-primary/50 via-white to-primary/20 p-5 sm:p-8 lg:border-b-0 lg:border-r">
              <div>
                {isLaunchFreeBilling ? (
                  <span className="inline-flex rounded-full border border-mainColor/25 bg-mainColor/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-mainColor">
                    Early joiner offer
                  </span>
                ) : null}
                <div className="mt-4 flex items-start justify-between gap-3">
                  <h3 className="text-2xl font-semibold leading-tight text-dark-black">{name}</h3>
                  {statusBadge}
                </div>
                <div className="mt-6 flex items-end gap-2">
                  <span className="text-[44px] font-semibold leading-none text-mainColor">
                    {currencySymbol}
                    {amount}
                  </span>
                  <span className="pb-2 text-sm text-dark-grey">{intervalLabel}</span>
                </div>
                {showDiscount ? (
                  <div className="mt-2 flex items-center gap-2 text-xs text-dark-grey">
                    <span className="line-through">
                      {currencySymbol}
                      {oldPriceDisplay ?? amount}
                    </span>
                    <span className="rounded-lg bg-mainColor px-1.5 py-0.5 text-white">
                      Save {discount}%
                    </span>
                  </div>
                ) : null}
                {billedLine ? (
                  <p className="mt-2 text-xs font-semibold text-dark-grey">{billedLine}</p>
                ) : null}
                {description ? (
                  <p className="mt-4 text-sm leading-relaxed text-dark-grey">{description}</p>
                ) : null}
              </div>
              <div className="mt-8">
                {planActionButton}
                {footerLine ? (
                  <p className="mt-3 text-center text-[11px] text-dark-grey">{footerLine}</p>
                ) : null}
              </div>
            </div>

            <div className="p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-mainColor">
                Everything included
              </p>
              <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                {features.map((feature, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2.5 text-[13px] leading-snug text-dark-grey"
                  >
                    <FaCheckCircle size={14} className="mt-[2px] shrink-0 text-mainColor" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {cancelConfirmModal}
      <div className="relative flex h-full min-h-[420px] flex-col rounded-[8px] border border-light-grey bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.06)]">
        <div className="flex flex-1 flex-col">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-dark-black">{name}</h3>
            {statusBadge}
          </div>
          <div className="mt-3 flex flex-1 flex-col gap-2">
            <div className="flex items-end">
              <p className="flex items-center gap-2 text-2xl font-semibold text-mainColor">
                {showDiscount && (
                  <span className="text-xs line-through text-gray-400">
                    {currencySymbol}
                    {oldPriceDisplay ?? amount}
                  </span>
                )}
                {currencySymbol}
                {amount}
              </p>{" "}
              {intervalLabel}
              {showDiscount && (
                <small className="ml-2 rounded-lg bg-mainColor px-[4px] text-xs text-white">
                  Save {discount}%
                </small>
              )}
            </div>
            {billedLine ? (
              <p className="text-xs font-semibold text-dark-grey">{billedLine}</p>
            ) : null}
            <div className="mt-1 flex flex-1 flex-col gap-2.5 overflow-auto pr-1">
              {features.map((feature, index) => (
                <p className="flex items-start gap-2 text-sm text-dark-grey" key={index}>
                  <FaCheckCircle size={14} className="mt-[2px] shrink-0 text-mainColor" />
                  <span>{feature}</span>
                </p>
              ))}
            </div>
          </div>
          {planActionButton ? <div className="mt-4">{planActionButton}</div> : null}
        </div>
      </div>
      {planChangeModal}
      {emailRequiredModal}
    </>
  );
};

export default PlanCard;
